import { Accordion } from "@/components/ui/accordion";
import { useCanvasPermissions } from "@/pages/canvas/hooks/useCanvasPermissions";
import { Frame, MessageCircle, MessagesSquare } from "lucide-react";
import { useCallback, useState } from "react";
import { RailGroup } from "../rail/RailGroup";
import { RailGroupActionButton } from "../rail/RailGroupActionButton";
import { RailZeroState } from "../rail/RailZeroState";
import {
  anchorCenterFromElement,
  dispatchCanvasToggleAddContext,
} from "./canvasRailAddContextEvents";
import { CanvasRailChats } from "./CanvasRailChats";
import { CanvasRailComments } from "./CanvasRailComments";
import { CanvasRailContents } from "./CanvasRailContents";
import { CanvasRailContentsControls } from "./CanvasRailContentsControls";
import { canvasRailCopy } from "./canvasRailCopy";
import { CanvasRailOsDropZone } from "./CanvasRailOsDropZone";
import {
  dispatchClearExplorerSelection,
  targetIsExplorerRow,
  useClearExplorerSelectionListener,
} from "./canvasRailSelectionEvents";
import { useCanvasRailComments } from "./useCanvasRailComments";
import { useCanvasRailContents } from "./useCanvasRailContents";
import { useCanvasRailNewChat } from "./useCanvasRailNewChat";

/**
 * The canvas-scoped drawer body: Chats, Contents, then Comments (T4's
 * simplified shape). Hosted by the rail drawer for whichever canvas is (or
 * was last) open — the caller owns the container identity.
 */
export function CanvasRailBody({ canvasId }: { canvasId: string }) {
  // Search retired from the drawer (nested pops will carry retrieval);
  // contents receive an empty query.
  const [searchQuery] = useState("");
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set(),
  );

  const { canEdit, canComment } = useCanvasPermissions(canvasId || undefined);
  const contents = useCanvasRailContents(canvasId, searchQuery);
  const comments = useCanvasRailComments(canvasId);
  const { handleNewChat, isCreatingChat } = useCanvasRailNewChat(
    canvasId,
    canEdit,
  );

  const clearLocalSelections = useCallback(() => {
    setSelectedFileIds((prev) => (prev.size > 0 ? new Set() : prev));
    setSelectedNodeIds((prev) => (prev.size > 0 ? new Set() : prev));
  }, []);
  useClearExplorerSelectionListener(clearLocalSelections);

  const handlePaneBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (targetIsExplorerRow(e.target)) return;
    dispatchClearExplorerSelection();
  }, []);

  const handleAddContext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      dispatchCanvasToggleAddContext(
        canvasId,
        anchorCenterFromElement(e.currentTarget),
      );
    },
    [canvasId],
  );

  return (
    <>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        onClick={handlePaneBackgroundClick}
      >
        <CanvasRailOsDropZone
          onFiles={null}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2"
          data-onboarding-target="canvas-explorer-contents"
        >
          <Accordion
            type="multiple"
            // Chats collapsed at rest, Contents open — the contents are why
            // the drawer is here; the closed Chats head carries its count
            // (DS 021).
            defaultValue={["canvas-contents"]}
            // gap-1: the same 4px rung the rail's workspaces run uses
            // between top-level places.
            className="flex flex-col gap-1"
          >
            <RailGroup
              groupId="canvas-chats"
              label="Chats"
              icon={MessagesSquare}
              noCap
              // Same head anatomy as Canvas Contents' add glyph: a
              // hover-revealed ＋ in the section head starts a chat here.
              hoverRevealRightSlot
              rightSlot={
                canEdit ? (
                  <RailGroupActionButton
                    label="New chat"
                    disabled={isCreatingChat}
                    onClick={() => void handleNewChat()}
                  />
                ) : undefined
              }
              items={[
                <CanvasRailChats
                  key="canvas-chats-body"
                  canvasId={canvasId}
                  searchQuery={searchQuery}
                  canEdit={canEdit}
                />,
              ]}
            />

            <RailGroup
              groupId="canvas-contents"
              label="Contents"
              icon={Frame}
              noCap
              hoverRevealRightSlot
              rightSlot={
                <CanvasRailContentsControls
                  canvasId={canvasId}
                  canEdit={canEdit}
                  hiddenKinds={contents.hiddenKinds}
                  onToggleKind={contents.toggleKind}
                  sort={contents.sort}
                  onChangeSort={(next) => contents.setSort(next)}
                  onAddContext={handleAddContext}
                />
              }
              items={
                contents.isContentsLoading || contents.isEmpty
                  ? []
                  : [
                      <CanvasRailContents
                        key="canvas-contents-body"
                        canvasId={canvasId}
                        canEdit={canEdit}
                        contents={contents}
                        selectedNodeIds={selectedNodeIds}
                        onSelectionChange={setSelectedNodeIds}
                        selectedFileIds={selectedFileIds}
                        onFileSelectionChange={setSelectedFileIds}
                      />,
                    ]
              }
              emptyState={
                contents.isContentsLoading ? undefined : (
                  <RailZeroState
                    icon={Frame}
                    accent="teal"
                    title={canvasRailCopy.empty.title}
                    description={canvasRailCopy.empty.description}
                    variant={canEdit ? "cta" : "passive"}
                    action={
                      canEdit
                        ? {
                            label: canvasRailCopy.empty.cta,
                            onClick: handleAddContext,
                          }
                        : undefined
                    }
                  />
                )
              }
            />

            {canComment && (
              <RailGroup
                groupId="canvas-comments"
                label="Comments"
                icon={MessageCircle}
                noCap
                items={
                  comments.isEmpty
                    ? []
                    : [
                        <CanvasRailComments
                          key="canvas-comments-body"
                          items={comments.items}
                        />,
                      ]
                }
                emptyState={
                  comments.threadsSynced ? (
                    <RailZeroState
                      icon={MessageCircle}
                      accent="rust"
                      title="No comments yet."
                      description="Press Q on the canvas to leave one."
                    />
                  ) : undefined
                }
              />
            )}
          </Accordion>
        </CanvasRailOsDropZone>
      </div>
    </>
  );
}
